import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import UserAvatar from './UserAvatar';
import {
  Sparkles, Target, ListChecks, TrendingUp, AlertTriangle,
  CheckCircle2, Clock, ChevronRight, Lightbulb, RefreshCw,
  Loader2, Award, Flame, Shield, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * My EOS Coach Widget - Personalized EOS coaching insights
 * Can be used for individual users or GM aggregate view
 */
const EOSCoachWidget = ({ 
  userId = null,  // null = current user, or specific user ID for GM view
  isGMView = false,
  teamFilter = null,
  compact = false,
  className = ""
}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [coaching, setCoaching] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCoaching();
  }, [userId]);

  const fetchCoaching = async () => {
    try {
      setError(null);
      const endpoint = userId ? `/eos/coaching/user/${userId}` : '/eos/coaching/me';
      const response = await axios.get(`${API}${endpoint}`);
      setCoaching(response.data);
    } catch (err) {
      console.error('Error fetching coaching:', err);
      setError('Unable to load coaching data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCoaching();
    setRefreshing(false);
    toast.success('Coaching insights updated');
  };

  const getHealthColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 8) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 6) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUp className="h-3 w-3 text-green-600" />;
    if (trend === 'down') return <ArrowDown className="h-3 w-3 text-red-600" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            <p className="text-sm text-muted-foreground mt-2">Loading your EOS Coach...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-yellow-600 mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-3">
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = coaching || {};
  const isMock = data.is_mock;

  // Compact view for sidebar
  if (compact) {
    return (
      <Card className={`border-purple-200 dark:border-purple-900 ${className}`}>
        <CardHeader className="py-3 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              My EOS Coach
            </CardTitle>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="py-2 space-y-3">
          {/* Health Score */}
          <div className={`p-2 rounded-lg ${getHealthBg(data.health_score || 7)}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Health Score</span>
              <span className={`text-lg font-bold ${getHealthColor(data.health_score || 7)}`}>
                {data.health_score || 7}/10
              </span>
            </div>
          </div>

          {/* This Week's Focus */}
          {data.this_weeks_focus?.length > 0 && (
            <div>
              <div className="text-xs font-medium mb-1 flex items-center gap-1">
                <Lightbulb className="h-3 w-3 text-yellow-500" />
                This Week&apos;s Focus
              </div>
              <div className="space-y-1">
                {data.this_weeks_focus.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-xs p-1.5 bg-muted rounded flex items-start gap-2">
                    <ChevronRight className="h-3 w-3 mt-0.5 text-purple-600 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {data.warnings?.length > 0 && (
            <div>
              <div className="text-xs font-medium mb-1 text-orange-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Attention Needed
              </div>
              <div className="space-y-1">
                {data.warnings.slice(0, 2).map((warning, idx) => (
                  <div key={idx} className="text-xs p-1.5 bg-orange-50 dark:bg-orange-950/30 rounded text-orange-800 dark:text-orange-200">
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="text-center p-1.5 bg-muted rounded">
              <Target className="h-3 w-3 mx-auto text-purple-600 mb-0.5" />
              <div className="text-xs font-medium">{data.rocks_status?.on_track || 0}</div>
              <div className="text-[10px] text-muted-foreground">Rocks</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <ListChecks className="h-3 w-3 mx-auto text-cyan-600 mb-0.5" />
              <div className="text-xs font-medium">{data.todos_status?.pending || 0}</div>
              <div className="text-[10px] text-muted-foreground">To-Dos</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <TrendingUp className="h-3 w-3 mx-auto text-green-600 mb-0.5" />
              <div className="text-xs font-medium flex items-center justify-center gap-0.5">
                {data.scorecard_health?.green || 0}
                {getTrendIcon(data.scorecard_health?.trend)}
              </div>
              <div className="text-[10px] text-muted-foreground">Green</div>
            </div>
          </div>

          {isMock && (
            <div className="text-[10px] text-center text-muted-foreground pt-1">
              AI Coaching unavailable - configure API key
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <Card className={`border-purple-200 dark:border-purple-900 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              My EOS Coach
              {isMock && <Badge variant="outline" className="text-xs">Demo Mode</Badge>}
            </CardTitle>
            <CardDescription>{data.health_summary || 'Personalized EOS insights and recommendations'}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Health Score & Quick Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${getHealthBg(data.health_score || 7)}`}>
            <div className="text-sm text-muted-foreground mb-1">Health Score</div>
            <div className={`text-3xl font-bold ${getHealthColor(data.health_score || 7)}`}>
              {data.health_score || 7}<span className="text-lg text-muted-foreground">/10</span>
            </div>
          </div>
          
          <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Target className="h-4 w-4 text-purple-600" />
              Rocks
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-green-600">{data.rocks_status?.on_track || 0}</span>
              <span className="text-sm text-muted-foreground">on track</span>
            </div>
            {(data.rocks_status?.at_risk || 0) > 0 && (
              <div className="text-xs text-red-600 mt-1">
                {data.rocks_status.at_risk} at risk
              </div>
            )}
          </div>
          
          <div className="p-4 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <ListChecks className="h-4 w-4 text-cyan-600" />
              To-Dos
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{data.todos_status?.pending || 0}</span>
              <span className="text-sm text-muted-foreground">pending</span>
            </div>
            {(data.todos_status?.overdue || 0) > 0 && (
              <div className="text-xs text-red-600 mt-1">
                {data.todos_status.overdue} overdue
              </div>
            )}
          </div>
          
          <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Scorecard
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">{data.scorecard_health?.green || 0}</span>
              {getTrendIcon(data.scorecard_health?.trend)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {data.scorecard_health?.yellow || 0} yellow • {data.scorecard_health?.red || 0} red
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* This Week's Focus */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              This Week&apos;s Focus
            </h4>
            {data.this_weeks_focus?.length > 0 ? (
              <div className="space-y-2">
                {data.this_weeks_focus.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                    <div className="h-5 w-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No specific focus items for this week.</p>
            )}
          </div>

          {/* Recommendations */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Recommendations
            </h4>
            {data.recommendations?.length > 0 ? (
              <div className="space-y-2">
                {data.recommendations.slice(0, 4).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 text-sm">
                    <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recommendations at this time.</p>
            )}
          </div>
        </div>

        {/* Warnings */}
        {data.warnings?.length > 0 && (
          <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
            <h4 className="font-semibold mb-2 flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <AlertTriangle className="h-4 w-4" />
              Attention Required
            </h4>
            <div className="space-y-2">
              {data.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-orange-800 dark:text-orange-200">
                  <span className="text-orange-500">•</span>
                  <span>{typeof warning === 'string' ? warning : (warning.message || warning.description || JSON.stringify(warning))}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risks */}
        {data.risks?.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4 text-red-600" />
              Risk Areas
            </h4>
            <div className="space-y-2">
              {data.risks.map((risk, idx) => {
                // Handle both object and string formats
                const riskArea = typeof risk === 'string' ? risk : (risk.area || 'Risk');
                const riskSeverity = typeof risk === 'object' ? (risk.severity || 'medium') : 'medium';
                const riskDescription = typeof risk === 'string' ? risk : (risk.description || '');
                const riskRecommendation = typeof risk === 'object' ? risk.recommendation : null;
                
                return (
                  <div key={idx} className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={riskSeverity === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                        {riskArea}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{riskSeverity}</Badge>
                    </div>
                    {riskDescription && <p className="text-sm text-muted-foreground">{riskDescription}</p>}
                    {riskRecommendation && (
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        → {riskRecommendation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Encouragement */}
        {data.encouragement && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              <p className="text-sm italic">{data.encouragement}</p>
            </div>
          </div>
        )}

        {/* Streaks */}
        {data.streaks && Object.keys(data.streaks).length > 0 && Array.isArray(data.streaks.positive_patterns) && (
          <div className="flex items-center gap-4 text-sm">
            {data.streaks.positive_patterns.map((pattern, idx) => (
              <div key={idx} className="flex items-center gap-1 text-green-600">
                <Flame className="h-4 w-4" />
                <span>{typeof pattern === 'string' ? pattern : JSON.stringify(pattern)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EOSCoachWidget;
